import React, { useState, useEffect } from "react";
import {useNavigate} from "react-router-dom";
import {
    Grid,
    TextField,
    Button,
    CircularProgress,
    Box,
    IconButton,
    FormControl,
    Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from "@mui/icons-material/Check";
import SoftBox from "components/SoftBox";
import SoftTypography      from "components/SoftTypography";
import DashboardLayout     from "reusableComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar     from "reusableComponents/Navbars/DashboardNavbar";
import Footer              from "reusableComponents/Footer";
import { useTranslation, Trans }  from "react-i18next";
import {saveTranslation, translateText} from "../../axios-client";
import CustomSelect from "reusableComponents/Select/CustomSelect";
import data from "../../constants/languages.json";
import SnackbarNotification from "reusableComponents/Snackbar/SnackbarNotification";

const TranslationComponent = () => {
    const { t, i18n } = useTranslation("fastTranslation");
    const dir = i18n.dir();
    const currentLang = i18n.language

    const [text, setText]         = useState("");
    const [loading, setLoading]   = useState(false);
    const [saveLoading, setSaveLoading]   = useState(false);
    const [translations, setTranslations] = useState([]);
    const [language, setLanguage] = useState('Arabic');
    const [languages, setLanguages] = useState([]);
    const [copiedStates, setCopiedStates] = useState({});
    const [textHistory, setTextHistory] = useState([""]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [notification, setNotification] = useState({show: false, snackbarMessage: '', severity: ""})

    const digitalDatabase = t('digitalDatabase', {ns:'sidenav'});

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (translations?.length > 0) {
                const message = t('saveBeforeQuit');
                e.returnValue = message;
                return message;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [translations]);

    useEffect(() => {
        setLanguages(
            data.map(lang => ({
                ...lang,
                label: i18n.language === 'ar'
                    ? lang.language_ar || lang.language
                    : lang.language
            }))
        );

    }, []);

    const handleTranslatedTextChange = (newText, index) => {
        setTranslations((prev) => {
            const updated = [...prev];
            if (!updated[index]) {
                updated[index] = { id: "", txt: "", words: 0 };
            }
            updated[index].txt = newText;
            updated[index].words = newText.trim() ? newText.trim().split(/\s+/).length : 0;
            return updated;
        });
    };

    const handleTranslate = () => {
        if (!text.trim()) return;
        setLoading(true);

        const targetLang =
            languages.find(l => l.code === language)?.label.en || language;

        translateText(text, targetLang).then(response => {

            const data = response.data;
            const keyValues =
                {
                    "translation1": 'translation',
                    "translation2": 'alternative1',
                    "translation3": 'alternative2'
                }
            const mapped = ["translation1", "translation2", "translation3"].map(k => ({
                id   : keyValues[k],
                txt  : data[k]?.translatedText || "",
                words: data[k]?.wordsCount ?? 0,
            }));
            setTranslations(mapped);

        }).catch(error => {
            setNotification({
                show: true,
                snackbarMessage: t("translationError"),
                severity: 'error'
            });

        }).finally(() => setLoading(false))

    };

    const handleSave = (translatedText) => {

        setSaveLoading(true)

        const payload = {
            'target_language': language,
            'ottoman_text': text,
            'translated_text': translatedText
        }
        saveTranslation(payload).then(res => {

                setNotification({
                    show: true,
                    snackbarMessage: <Trans
                        ns="fastTranslation"
                        i18nKey="translationSaved"
                        values={{digitalDatabase}}
                        components={[<></>, <a key={'digitalDatabase'} href={`/${currentLang}/documents`}/>]}
                    />,
                    severity: 'success'
                })
            setText("")
            setTranslations([])
        }
        ).catch(error => {
            setNotification({
                show: true,
                snackbarMessage: t("saveTranslationError"),
                severity: 'error'
            });

        }).finally(() => setSaveLoading(false))


    }
    const handleCopy = async (txt, translationId) => {
        if (!txt) return;

        try {
            await navigator.clipboard.writeText(txt);
            setCopiedStates(prev => ({ ...prev, [translationId]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [translationId]: false }));
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const handleTextChange = (e) => {
        const inputText = e.target.value;
        const truncatedText = inputText.length > MAX_CHARS ? inputText.slice(0, MAX_CHARS) : inputText;
        setText(truncatedText);
        const newHistory = textHistory.slice(0, historyIndex + 1);
        newHistory.push(truncatedText);
        setTextHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const currentText = text;
        const selection = e.target.selectionStart;
        const selectionEnd = e.target.selectionEnd;
        const beforeSelection = currentText.slice(0, selection);
        const afterSelection = currentText.slice(selectionEnd);
        const newText = beforeSelection + pastedText + afterSelection;
        const truncatedText = newText.length > MAX_CHARS ? newText.slice(0, MAX_CHARS) : newText;

        setText(truncatedText);

        const newHistory = textHistory.slice(0, historyIndex + 1);
        newHistory.push(truncatedText);
        setTextHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            if (historyIndex > 0) {
                const newIndex = historyIndex - 1;
                setText(textHistory[newIndex]);
                setHistoryIndex(newIndex);
            }
        }

        if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
            e.preventDefault();
            if (historyIndex < textHistory.length - 1) {
                const newIndex = historyIndex + 1;
                setText(textHistory[newIndex]);
                setHistoryIndex(newIndex);
            }
        }
        if (e.ctrlKey && e.key === 'c') {
            return;
        }
        if (e.ctrlKey && e.key === 'v') {
            return;
        }
    };
    const CARD_HEIGHT = 400;
    const MAX_CHARS   = 2000;
    const charCount   = text.length;
    const isOverLimit = charCount > MAX_CHARS;

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <SoftBox py={3} px={{ xs: 2, md: 3}} bgcolor="#f9fafb" dir={dir}>
                <SoftBox textAlign="center" mb={3}>
                    <SoftTypography variant="caption" fontWeight="light" >
                        {t("description")}
                    </SoftTypography>
                </SoftBox>

                <Grid container spacing={3} justifyContent="center" >
                    <Grid item xs={6} md={3} lg={6}>
                        <SoftBox
                            p={2}
                            bgcolor="white"
                            borderRadius="m"
                            sx={{
                                mx: "auto",
                                position: { md: "sticky" },
                                top: { md: 80 },
                                border: "1px solid #e0e0e0",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            }}
                        >
                            <FormControl size="small" fullWidth sx={{ mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px'}}>
                                <label className="label-style">
                                    {t("targetLanguage")}:
                                </label>
                                <CustomSelect
                                    value={language}
                                    onChange={(value) => setLanguage(value)}
                                    placeholder={t("language")}
                                    options={languages
                                        .map((languageData) => ({
                                            value: languageData.language,
                                            label: i18n.language === 'ar' ? languageData.language_ar || languageData.language : languageData.language,
                                        }))}
                                    dropdown={'bottom'}
                                />
                            </FormControl>
                            <Box position="relative">
                                <TextField
                                    multiline
                                    maxRows={12}
                                    minRows={14}
                                    fullWidth
                                    placeholder={t("placeholder")}
                                    value={text}
                                    onChange={handleTextChange}
                                    onPaste={handlePaste}
                                    onKeyDown={handleKeyDown}
                                    error={isOverLimit}
                                    InputProps={{
                                        style: { direction: "rtl" },
                                        inputProps: { maxLength: MAX_CHARS }
                                    }}
                                    sx={{
                                        height: CARD_HEIGHT,
                                        "& .MuiOutlinedInput-root": {
                                            height: "100%",
                                            alignItems: "flex-start",
                                            borderRadius: "12px",
                                            ...(charCount === MAX_CHARS && {
                                                "& fieldset": {
                                                    borderColor: "#d32f2f",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "#d32f2f",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#d32f2f",
                                                },
                                            }),
                                            "& textarea": {
                                                width: "100% !important",
                                                overflowY: "auto !important",
                                                resize: "none",
                                            },
                                        },
                                        "& .MuiInputBase-input": {
                                            fontSize: "1.05rem",
                                            lineHeight: 1.6,
                                            p: "12px",
                                            paddingBottom: "32px",
                                            overflowY: "auto",
                                            ...(charCount === MAX_CHARS && {
                                                color: "#d32f2f",
                                            }),
                                        },
                                    }}
                                />
                                <SoftTypography
                                    variant="caption"
                                    sx={{
                                        position: "absolute",
                                        bottom: 16,
                                        [dir === "rtl" ? "right" : "left"]: 16,
                                        color: charCount === MAX_CHARS ? "#d32f2f" : "text.secondary",
                                        fontWeight: charCount === MAX_CHARS ? "bold" : "normal",
                                    }}
                                >
                                    {charCount}/{MAX_CHARS}
                                </SoftTypography>
                            </Box>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleTranslate}
                                disabled={loading || isOverLimit}
                                sx={{
                                    py: 1.5,
                                    backgroundColor: "#c713a0",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#b01290",
                                    },
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                                ) : (
                                    t("translateNow")
                                )}
                            </Button>

                        </SoftBox>
                    </Grid>
                    <Grid item xs={6} md={3} lg={6}>
                        <SoftBox sx={{ mx: "auto" }}>
                            <Grid container spacing={2}>
                                {["translation", "alternative1", "alternative2"].map((label, idx) => {
                                    const item = translations[idx] || { txt: "", words: 0 };
                                    const isCopied = copiedStates[label];
                                    const translationItem = item.id == 'translation';

                                    return (
                                        <Grid item xs={12} key={label}>
                                            <SoftBox
                                                position="relative"
                                                bgcolor="white"
                                                borderRadius="m"
                                                sx={{
                                                    height: CARD_HEIGHT,
                                                    border: "1px solid #e0e0e0",
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                                }}
                                            >
                                                <Tooltip
                                                    title={isCopied ? t('copied') : t('copy')}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopy(item.txt, label)}
                                                        sx={{
                                                            position: "absolute",
                                                            bottom: 8,
                                                            right: translationItem ? 40 : 8,
                                                            backgroundColor: isCopied ? "#4caf50" : "rgba(255, 255, 255, 0.9)",
                                                            color: isCopied ? "#fff" : "inherit",
                                                            zIndex: 1,
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                            "&:hover": {
                                                                backgroundColor: isCopied ? "#4caf50" : "rgba(255, 255, 255, 0.9)",
                                                            },
                                                        }}
                                                    >
                                                        {isCopied ? (
                                                            <CheckIcon fontSize="small" />
                                                        ) : (
                                                            <ContentCopyIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                {translationItem && (<Tooltip
                                                        title={t('save')}
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSave(item.txt)}
                                                            sx={{
                                                                position: "absolute",
                                                                bottom: 8,
                                                                right: 8,
                                                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                                color: "inherit",
                                                                zIndex: 1,
                                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                                "&:hover": {
                                                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                                },
                                                            }}
                                                        >
                                                            {saveLoading ?
                                                                <CircularProgress size={12} sx={{ color: "#fff" }} />
                                                                : <SaveIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <SoftBox
                                                    p={3}
                                                    sx={{
                                                        height: "100%",
                                                        overflowY: "auto",
                                                        paddingBottom: "50px",
                                                    }}
                                                >
                                                    <SoftTypography variant="h6" fontWeight="medium" mb={1}
                                                                    sx={{ textAlign: dir === "rtl" ? "right" : "left" }}>
                                                        {t(label)} {item.words ? `(${item.words} ${t("words")})` : ""}
                                                    </SoftTypography>

                                                    {item.txt && item.id == 'translation' ? (
                                                        <TextField
                                                            value={item.txt}
                                                            onChange={(e) => handleTranslatedTextChange(e.target.value, idx)}
                                                            multiline
                                                            minRows={10}
                                                            maxRows={15}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                '& .MuiInputBase-input': {
                                                                    textAlign: dir === 'rtl' ? 'right' : 'left',
                                                                },
                                                                "& .MuiOutlinedInput-root": {
                                                                    "& textarea": {
                                                                        width: "100% !important",
                                                                        overflowY: "auto !important",
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <SoftTypography
                                                            variant="body2"
                                                            fontWeight="light"
                                                            sx={{ lineHeight: 1.6, textAlign: dir === "rtl" ? "right" : "left" }}
                                                        >
                                                            {item.txt}
                                                        </SoftTypography>
                                                    )}
                                                </SoftBox>
                                            </SoftBox>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </SoftBox>
                    </Grid>
                </Grid>
            </SoftBox>

            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />

            <Footer />
        </DashboardLayout>
    );
};

export default TranslationComponent;